import { ServerMessagePayload } from '@common/api';
import { Graphics } from 'pixi.js';

import { ClientGlobalSystem } from './ClientBase';

const GRID_COLOR = 0x444444;
const GRID_ALPHA = 0.5;

/**
 * Debug system that renders tile grid lines overlay.
 * Toggled via the Debug Panel.
 */
export class DebugGridLinesRenderSystem extends ClientGlobalSystem {
  private graphics: Graphics | null = null;
  private initialized = false;
  private lastShowState = false;

  run(): ServerMessagePayload[] {
    const world = this.getWorld();
    const chunkDataService = this.clientServices.chunkData;
    const showGridLines = this.clientServices.debug.state.showGridLines;

    // Handle visibility toggle
    if (this.graphics) {
      this.graphics.visible = showGridLines;
    }

    // Early exit if nothing to show
    if (!showGridLines) {
      this.lastShowState = false;
      return [];
    }

    // Get local player's scene
    const localEntityId = this.clientServices.localPlayer.entityId;
    const localPlayerTransform = localEntityId
      ? world.getComponent(localEntityId, 'Transform')
      : null;
    const currentSceneId = localPlayerTransform?.sceneId;
    if (!currentSceneId) return [];

    // Get chunks
    const chunks = chunkDataService.getChunks(currentSceneId);
    if (!chunks) return [];

    // Create graphics if not exists
    if (!this.graphics) {
      this.graphics = new Graphics();
      this.graphics.zIndex = 99998; // Below collision grid
      this.clientServices.render.stage?.addChild(this.graphics);
    }

    // Only redraw when toggle changes or first time
    const needsRedraw = !this.initialized || this.lastShowState !== showGridLines;
    if (!needsRedraw) return [];

    this.initialized = true;
    this.lastShowState = showGridLines;

    const metadata = chunkDataService.getSceneMetadata(currentSceneId);
    if (!metadata) return [];

    const CHUNK_SIZE = metadata.chunkSize;
    const tileWidth = metadata.tileWidth;
    const tileHeight = metadata.tileHeight;

    this.graphics.clear();

    // Draw grid lines for all loaded chunks
    for (const [, chunkData] of chunks) {
      const chunkWorldX = chunkData.chunkX * CHUNK_SIZE * tileWidth;
      const chunkWorldY = chunkData.chunkY * CHUNK_SIZE * tileHeight;

      // Draw horizontal lines across the chunk
      for (let row = 0; row <= CHUNK_SIZE; row++) {
        const y = chunkWorldY + row * tileHeight;
        this.graphics.moveTo(chunkWorldX, y);
        this.graphics.lineTo(chunkWorldX + CHUNK_SIZE * tileWidth, y);
      }
      // Draw vertical lines across the chunk
      for (let col = 0; col <= CHUNK_SIZE; col++) {
        const x = chunkWorldX + col * tileWidth;
        this.graphics.moveTo(x, chunkWorldY);
        this.graphics.lineTo(x, chunkWorldY + CHUNK_SIZE * tileHeight);
      }
    }
    this.graphics.stroke({ color: GRID_COLOR, alpha: GRID_ALPHA, width: 1 });

    return [];
  }
}
